import { useAuth } from "@/contexts/AuthContext";
import { useChapterStats } from "@/api/chapters";
import {
  Target,
  Building2,
  User,
  Banknote,
  ArrowUpRight,
  ArrowRightLeft,
  GraduationCap,
  CalendarDays,
  BarChart3,
  Heart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";

/* ── Circular Progress Ring ── */
function ProgressRing({
  value,
  max,
  size = 110,
  strokeWidth = 9,
  color = "hsl(var(--primary))",
  label = "completed",
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const offset = circumference * (1 - pct);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold tabular-nums leading-none">
          {Math.round(pct * 100)}%
        </span>
        <span className="text-[10px] text-muted-foreground mt-0.5">
          {label}
        </span>
      </div>
    </div>
  );
}

/* ── Mini bar ── */
function MiniBar({ value, max, className = "bg-emerald-500" }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  return (
    <div className="h-1.5 bg-secondary rounded-full overflow-hidden flex-1">
      <div
        className={`h-full rounded-full transition-all duration-500 ${className}`}
        style={{ width: `${pct * 100}%` }}
      />
    </div>
  );
}

/* ── Stat Pill ── */
function StatPill({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${color}`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {sub && (
          <p className="text-[11px] text-muted-foreground truncate">{sub}</p>
        )}
      </div>
      <span className="text-sm font-bold tabular-nums shrink-0">{value}</span>
    </div>
  );
}

const fmt = (n) => (n || 0).toLocaleString("en-IN");
const fmtK = (n) => {
  if (!n) return "₹0";
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${n}`;
};

export default function Overview() {
  const { user } = useAuth();
  const { data: stats, isLoading } = useChapterStats();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-44 w-full rounded-xl" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
        </div>
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const {
    target,
    acquired,
    collected,
    conversion,
    categories,
    mahalluBreakdown,
    activity,
  } = stats;

  const targetPctStudents =
    target.studentCount > 0
      ? Math.round((acquired.studentCount / target.studentCount) * 100)
      : 0;
  const targetPctAmount =
    target.amountINR > 0
      ? Math.round((acquired.amountINR / target.amountINR) * 100)
      : 0;
  const collectedPctOfTarget =
    target.amountINR > 0
      ? Math.round((collected.amountINR / target.amountINR) * 100)
      : 0;

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center justify-between px-4 h-14">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Overview</h1>
            <p className="text-xs text-muted-foreground">{user?.chapterName}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* ════════════════════════════════════════════
            SECTION 1: TARGET COMPLETION
        ════════════════════════════════════════════ */}

        {/* ── Hero Target Card ── */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <Target className="h-4 w-4 text-primary" />
              Target Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-5">
            <div className="flex items-center gap-5">
              <ProgressRing
                value={acquired.studentCount}
                max={target.studentCount || 1}
                label="acquired"
              />
              <div className="flex-1 min-w-0 space-y-2">
                {/* Student count */}
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold tabular-nums">
                      {fmt(acquired.studentCount)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      / {fmt(target.studentCount)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    students acquired
                  </p>
                </div>
                {/* Amount */}
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-lg font-bold tabular-nums">
                      {fmtK(acquired.amountINR)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      / {fmtK(target.amountINR)}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    amount acquired (INR equiv.)
                  </p>
                </div>
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="text-[10px] tabular-nums gap-1"
                  >
                    <ArrowUpRight className="h-3 w-3" />
                    {targetPctStudents}% students
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-[10px] tabular-nums gap-1"
                  >
                    <Banknote className="h-3 w-3" />
                    {targetPctAmount}% amount
                  </Badge>
                </div>
              </div>
            </div>

            {/* Target progress bar */}
            <div className="mt-4 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>Acquired vs Target</span>
                <span className="tabular-nums">{targetPctAmount}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(targetPctAmount, 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Target Breakdown: Acquired + Collected ── */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <GraduationCap className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xl font-bold tabular-nums">
                  {fmt(acquired.studentCount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Students Acquired
                </p>
                <p className="text-[11px] font-medium text-blue-600 tabular-nums mt-0.5">
                  {fmtK(acquired.amountINR)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Banknote className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xl font-bold tabular-nums">
                  {fmt(collected.studentCount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Students Collected
                </p>
                <p className="text-[11px] font-medium text-emerald-600 tabular-nums mt-0.5">
                  {fmtK(collected.amountINR)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Acquired Source Breakdown ── */}
        <Card>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold">
              Acquisition Source
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-1">
            <StatPill
              label="From Sponsorships"
              value={`${fmt(acquired.fromSponsorships.studentCount)} students`}
              sub={fmtK(acquired.fromSponsorships.amountINR)}
              icon={Heart}
              color="bg-violet-100 text-violet-600"
            />
            <Separator />
            <StatPill
              label="Standalone Payments"
              value={`${fmt(acquired.fromStandalone.studentCount)} students`}
              sub={fmtK(acquired.fromStandalone.amountINR)}
              icon={Banknote}
              color="bg-sky-100 text-sky-600"
            />
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════════
            SECTION 2: CONVERSION RATE
        ════════════════════════════════════════════ */}

        <Card>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <ArrowRightLeft className="h-4 w-4 text-amber-500" />
              Sponsorship Conversion
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-5">
            <div className="flex items-center gap-5">
              <ProgressRing
                value={collected.fromSponsorships.amountINR}
                max={acquired.fromSponsorships.amountINR || 1}
                size={90}
                strokeWidth={8}
                color="hsl(var(--chart-2))"
                label="collected"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">
                    Amount Conversion
                  </p>
                  <p className="text-xl font-bold tabular-nums">
                    {conversion.amountRate}%
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {fmtK(collected.fromSponsorships.amountINR)} of{" "}
                    {fmtK(acquired.fromSponsorships.amountINR)} collected
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground font-medium">
                    Count Conversion
                  </p>
                  <p className="text-lg font-bold tabular-nums">
                    {conversion.countRate}%
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {conversion.countCompleted} of {conversion.countTotal}{" "}
                    sponsorships completed
                  </p>
                </div>
              </div>
            </div>

            {/* Status breakdown */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-lg font-bold tabular-nums text-amber-700">
                  {conversion.countPending}
                </p>
                <p className="text-[10px] text-muted-foreground">Pending</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-lg font-bold tabular-nums text-blue-700">
                  {conversion.countActive}
                </p>
                <p className="text-[10px] text-muted-foreground">Active</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                <p className="text-lg font-bold tabular-nums text-emerald-700">
                  {conversion.countCompleted}
                </p>
                <p className="text-[10px] text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════════
            SECTION 3: SPONSORSHIP STATS BY CATEGORY
        ════════════════════════════════════════════ */}

        <Card>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <BarChart3 className="h-4 w-4 text-violet-500" />
              Sponsorship by Category
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {/* Mahallu */}
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center">
                  <Building2 className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">Mahallu</p>
                  <p className="text-[11px] text-muted-foreground">
                    {categories.mahallu.count} sponsorships
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Promised
                  </p>
                  <p className="text-sm font-bold tabular-nums">
                    ₹{fmt(categories.mahallu.promised)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Collected
                  </p>
                  <p className="text-sm font-bold tabular-nums text-emerald-600">
                    ₹{fmt(categories.mahallu.paid)}
                  </p>
                </div>
              </div>
              <MiniBar
                value={categories.mahallu.paid}
                max={categories.mahallu.promised}
                className="bg-violet-500"
              />
              <p className="text-[10px] text-muted-foreground text-right tabular-nums">
                {categories.mahallu.promised > 0
                  ? Math.round(
                      (categories.mahallu.paid / categories.mahallu.promised) *
                        100,
                    )
                  : 0}
                % collected
              </p>
            </div>

            {/* Individual */}
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md bg-sky-100 text-sky-600 flex items-center justify-center">
                  <User className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">Individual</p>
                  <p className="text-[11px] text-muted-foreground">
                    {categories.individual.count} sponsorships
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Promised
                  </p>
                  <p className="text-sm font-bold tabular-nums">
                    ₹{fmt(categories.individual.promised)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Collected
                  </p>
                  <p className="text-sm font-bold tabular-nums text-emerald-600">
                    ₹{fmt(categories.individual.paid)}
                  </p>
                </div>
              </div>
              <MiniBar
                value={categories.individual.paid}
                max={categories.individual.promised}
                className="bg-sky-500"
              />
              <p className="text-[10px] text-muted-foreground text-right tabular-nums">
                {categories.individual.promised > 0
                  ? Math.round(
                      (categories.individual.paid /
                        categories.individual.promised) *
                        100,
                    )
                  : 0}
                % collected
              </p>
            </div>

            {/* Aggregate */}
            <Separator />
            <div className="rounded-lg bg-muted/50 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Aggregate
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold tabular-nums">
                    {categories.aggregate.count}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">
                    {fmtK(categories.aggregate.promised)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Promised</p>
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums text-emerald-600">
                    {fmtK(categories.aggregate.paid)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Collected</p>
                </div>
              </div>
              <MiniBar
                value={categories.aggregate.paid}
                max={categories.aggregate.promised}
                className="bg-emerald-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════════
            SECTION 4: MAHALLU CONVERSION
        ════════════════════════════════════════════ */}

        {mahalluBreakdown?.length > 0 && (
          <Card>
            <CardHeader className="pb-2 px-4 pt-4 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-violet-500" />
                Mahallu Conversion
              </CardTitle>
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-xs"
                onClick={() => navigate("/chapter/mahallu")}
              >
                View all
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {mahalluBreakdown.map((m, i) => (
                <div key={m.name} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-muted-foreground w-5 shrink-0">
                        {i + 1}.
                      </span>
                      <span className="text-sm font-medium truncate">
                        {m.name}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-[10px] shrink-0 tabular-nums"
                      >
                        {m.count}
                      </Badge>
                    </div>
                    <Badge
                      variant={
                        m.rate >= 75
                          ? "default"
                          : m.rate >= 40
                            ? "secondary"
                            : "outline"
                      }
                      className="text-[10px] tabular-nums shrink-0 ml-2"
                    >
                      {m.rate}%
                    </Badge>
                  </div>
                  <div className="pl-7 space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">
                        Promised: ₹{fmt(m.promised)}
                      </span>
                      <span className="text-emerald-600 font-medium">
                        Paid: ₹{fmt(m.paid)}
                      </span>
                    </div>
                    <MiniBar
                      value={m.paid}
                      max={m.promised}
                      className={
                        m.rate >= 75
                          ? "bg-emerald-500"
                          : m.rate >= 40
                            ? "bg-amber-500"
                            : "bg-red-400"
                      }
                    />
                  </div>
                  {i < mahalluBreakdown.length - 1 && (
                    <Separator className="mt-2" />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ════════════════════════════════════════════
            SECTION 5: ACTIVITY & KEY METRICS
        ════════════════════════════════════════════ */}

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">
                  {activity?.weekPayments || 0}
                </p>
                <p className="text-xs text-muted-foreground">This Week</p>
                <p className="text-[11px] font-medium text-emerald-600 tabular-nums mt-0.5">
                  ₹{fmt(activity?.weekAmount || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="h-8 w-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Banknote className="h-4 w-4" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">
                  {activity?.monthPayments || 0}
                </p>
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-[11px] font-medium text-emerald-600 tabular-nums mt-0.5">
                  ₹{fmt(activity?.monthAmount || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Key Metrics Row ── */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-3 text-center space-y-1">
              <p className="text-[11px] text-muted-foreground font-medium">
                Target
              </p>
              <p className="text-lg font-bold tabular-nums text-primary">
                {fmt(target.studentCount)}
              </p>
              <p className="text-[10px] text-muted-foreground">students</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center space-y-1">
              <p className="text-[11px] text-muted-foreground font-medium">
                Collected
              </p>
              <p className="text-lg font-bold tabular-nums text-emerald-600">
                {collectedPctOfTarget}%
              </p>
              <p className="text-[10px] text-muted-foreground">of target</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center space-y-1">
              <p className="text-[11px] text-muted-foreground font-medium">
                Payments
              </p>
              <p className="text-lg font-bold tabular-nums text-violet-600">
                {activity?.totalPayments || 0}
              </p>
              <p className="text-[10px] text-muted-foreground">total</p>
            </CardContent>
          </Card>
        </div>

        {/* ── Target Config Info ── */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>
                Cost/Student: ₹{target.costPerStudentINR} (INR) /{" "}
                {target.costPerStudentForeign} (Foreign)
              </span>
              <span className="tabular-nums">
                Target: {fmtK(target.amountINR)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
